import type { Cart, CartItemInterface, Maybe } from '@pylot-data/pylotschema'
import { GTMEvent } from '@airglobal/pylot-gtm/src/GTM'
import { itemHasKey, isNonNullRecord } from '@corratech/pylot-utils'
import { UpsellPromotionAdditionalData } from 'stores/ooka_uae_store/components/OOKA/pylot-gtm/src/GTMEvents/pushUpsellPromotionView'

type GTMCheckoutActionField = {
    step?: number
    option?: string
    actionField?: ActionField
    currency?: string
    cart?: Cart
    productsDetails?: [
        {
            id: string
            name: string
            price: number
            quantity: number
            category: string
            currencyCode: string
            variant: string
            brand: string
            brand_text?: string
            subscription_frequency: string
            creative_name?: string
            creative_slot?: string
            promotion_id?: string
            promotion_name?: string
        }
    ]
}

type CartProductWithBrand = NonNullable<CartItemInterface['product']> & {
    brand_text?: string
}

type localItemType = {
    sku: string
}

type productItemInterface = {
    price: number
    id: string
    subscription_frequency: string
    brand: number | string
    brand_text?: string
}

export type ActionField = {
    step: number
    action?: string
    option: string
    currencyCode?: string
    value: number | undefined | null
    coupon?: string
    discount?: number
}

const isValidActionField = (data: unknown): data is GTMCheckoutActionField => {
    if (!isNonNullRecord(data)) {
        return false
    }
    return itemHasKey(data, 'step') && !isNaN(Number(data.step))
}

export const checkoutOption = (data: GTMCheckoutActionField): string => {
    switch (data.step) {
        case 0:
            if (data?.option === 'email_id') {
                data.actionField!.action = 'signup'
                return data.actionField!.action
            }
            data.actionField!.action = 'login'
            return data.actionField!.action

        case 1:
            data.actionField!.action = 'cart'
            return data.actionField!.action

        case 2:
            data.actionField!.action = 'shipping'
            return data.actionField!.action

        case 3:
            data.actionField!.action = 'payment'
            return data.actionField!.action
        default:
            return ''
    }
}

export const formatCheckoutActionField = (
    data: unknown
): ActionField | undefined => {
    if (!isValidActionField(data)) {
        console.info('Invalid checkout option GTM data')
        return { step: 1, option: '', value: 0 }
    }
    data.actionField!.action = checkoutOption(data)
    data.actionField!.option = data.step === 1 ? '' : String(data.option)
    data.actionField!.currencyCode = data.currency
    data.actionField!.value = data?.cart?.prices!.grand_total!.value
    data.actionField!.coupon = data?.cart?.applied_coupons?.[0]?.code
    data.actionField!.discount =
        data?.cart?.prices?.discounts?.[0]?.amount?.value ?? 0
    return data.actionField
}

export const productDetailsField = (data: GTMCheckoutActionField) => {
    const productData = data?.productsDetails?.map(
        (item: productItemInterface) => {
            data?.cart?.items?.filter((cartItem: Maybe<CartItemInterface>) => {
                if (cartItem?.product.sku === item.id) {
                    const localItems =
                        JSON.parse(
                            localStorage.getItem('addedToCartItems') || '[]'
                        ) || []

                    const match = localItems.find(
                        (localItem: localItemType) => localItem.sku === item.id
                    )

                    if (match) {
                        const {
                            creative_name,
                            creative_slot,
                            promotion_id,
                            promotion_name
                        } = UpsellPromotionAdditionalData

                        Object.assign(item, {
                            creative_name: creative_name,
                            creative_slot: creative_slot,
                            promotion_id: promotion_id,
                            promotion_name: promotion_name
                        })
                    }

                    item.price = Number(item.price)
                    item.subscription_frequency =
                        cartItem?.subscription_data?.billing_period ?? 'NA'
                    item.brand =
                        (cartItem?.product as CartProductWithBrand)
                            ?.brand_text ??
                        item.brand_text ??
                        ''
                }
            })
            return item
        }
    )

    return productData
}

export const pushCheckoutStep = (data: GTMCheckoutActionField): GTMEvent => {
    return {
        event: 'checkout',
        ecommerce: {
            checkout: {
                actionField: formatCheckoutActionField(data),
                products: productDetailsField(data) ?? []
            }
        },
        _clear: true
    }
}
